export type CanonicalOrderStatus =
  | 'PLACED'
  | 'ACCEPTED'
  | 'PACKING'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'CANCELLED';

export type OrderStatusMeta = {
  value: CanonicalOrderStatus;
  label: string;
  badge: string;
};

const ORDER_STATUS_META: Record<CanonicalOrderStatus, OrderStatusMeta> = {
  PLACED: { value: 'PLACED', label: 'Placed', badge: 'secondary' },
  ACCEPTED: { value: 'ACCEPTED', label: 'Accepted', badge: 'primary' },
  PACKING: { value: 'PACKING', label: 'Packing', badge: 'info' },
  OUT_FOR_DELIVERY: { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', badge: 'dark' },
  DELIVERED: { value: 'DELIVERED', label: 'Delivered', badge: 'success' },
  CANCELLED: { value: 'CANCELLED', label: 'Cancelled', badge: 'danger' },
};

export const ORDER_STATUS_OPTIONS = [
  ORDER_STATUS_META.PLACED,
  ORDER_STATUS_META.ACCEPTED,
  ORDER_STATUS_META.PACKING,
  ORDER_STATUS_META.OUT_FOR_DELIVERY,
  ORDER_STATUS_META.DELIVERED,
  ORDER_STATUS_META.CANCELLED,
] as const;

export function normalizeOrderStatusValue(status?: string | null): CanonicalOrderStatus | string {
  const normalized = String(status || '').trim();
  if (!normalized) return '';

  const upper = normalized.toUpperCase();
  if (upper === 'PLACED' || upper === 'PENDING') return 'PLACED';
  if (upper === 'ACCEPTED' || upper === 'ASSIGNED') return 'ACCEPTED';
  if (upper === 'PACKING' || upper === 'PICKED' || upper === 'PROCESSING') return 'PACKING';
  if (upper === 'OUT_FOR_DELIVERY' || upper === 'SHIPPED') return 'OUT_FOR_DELIVERY';
  if (upper === 'DELIVERED' || upper === 'DELIVERED_PENDING_PAYMENT') return 'DELIVERED';
  if (upper === 'CANCELLED' || upper === 'FAILED') return 'CANCELLED';

  return upper;
}

export function getOrderStatusMeta(status?: string | null): OrderStatusMeta {
  const normalized = normalizeOrderStatusValue(status);
  if (normalized in ORDER_STATUS_META) {
    return ORDER_STATUS_META[normalized as CanonicalOrderStatus];
  }

  return {
    value: 'PLACED',
    label: String(status || 'Unknown'),
    badge: 'secondary',
  };
}

export function isDeliveredOrder(status?: string | null): boolean {
  return normalizeOrderStatusValue(status) === 'DELIVERED';
}
